/** 开发环境通过 Vite 代理到 ASP.NET（vite.config.ts）；生产可设 VITE_API_BASE */

import { ApiError } from "./errors";

const base = import.meta.env.VITE_API_BASE ?? "";

async function throwIfNotOk(res: Response): Promise<void> {
  if (res.ok) return;
  const text = await res.text();
  throw ApiError.fromResponse(res, text);
}

function shouldRetryUpdate(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 405);
}

function updateRequestPaths(path: string): string[] {
  const normalized = path.replace(/\/$/, "");
  return [normalized, `${normalized}/update`];
}

/** POST（及必要时 PUT）更新；IIS 拦截或旧版 API 时依次尝试 /update 备用路径。 */
async function fetchUpdate(path: string, body: unknown): Promise<Response> {
  const bodyStr = typeof body === "string" ? body : JSON.stringify(body);
  const headers = { "Content-Type": "application/json" };
  const [primary, update] = updateRequestPaths(path);
  const attempts: Array<{ p: string; method: "POST" | "PUT" }> = [
    { p: primary, method: "POST" },
    { p: update, method: "POST" },
    { p: primary, method: "PUT" },
  ];

  let lastError: unknown;
  for (const { p, method } of attempts) {
    const url = p.startsWith("http") ? p : `${base}${p}`;
    try {
      const res = await fetch(url, withAuth(p, { method, headers, body: bodyStr }));
      await throwIfNotOk(res);
      return res;
    } catch (e) {
      if (shouldRetryUpdate(e)) {
        lastError = e;
        continue;
      }
      throw e;
    }
  }
  throw lastError;
}

const PLATFORM_IMPERSONATION_KEY = "platform_impersonation";

function pathnameOnly(path: string): string {
  const p = path.startsWith("http") ? new URL(path).pathname : path.split("?")[0];
  return p;
}

function isAuthExcludedPath(pathname: string): boolean {
  return (
    pathname.endsWith("/super-admin/login") ||
    pathname.endsWith("/sub-admins/login") ||
    pathname.endsWith("/platform/login")
  );
}

/** 平台代管租户会话（与 bearerForPath 一致） */
export function isPlatformTenantImpersonating(): boolean {
  try {
    return !!sessionStorage.getItem(PLATFORM_IMPERSONATION_KEY);
  } catch {
    return false;
  }
}

/** 与 adminAuth / platformAuth 中存储的 key 一致 */
export const ADMIN_BEARER_KEY = "fanhub_admin_bearer";
export const PLATFORM_BEARER_KEY = "fanhub_platform_bearer";

function bearerForPath(path: string): string | null {
  const pathname = pathnameOnly(path);
  if (isAuthExcludedPath(pathname)) return null;

  const adminT = localStorage.getItem(ADMIN_BEARER_KEY);
  const platT = sessionStorage.getItem(PLATFORM_BEARER_KEY);

  if (pathname.startsWith("/api/platform")) return platT;

    if (isPlatformTenantImpersonating() && platT) return platT;

  if (adminT) return adminT;

  return platT;
}

function withAuth(path: string, init?: RequestInit): RequestInit {
  const token = bearerForPath(path);
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
}

export async function apiGet<T>(path: string): Promise<T> {
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, withAuth(path));
  await throwIfNotOk(res);
  return res.json() as Promise<T>;
}

export async function apiGetText(path: string): Promise<string> {
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, withAuth(path));
  await throwIfNotOk(res);
  return res.text();
}

export async function apiGetBlob(path: string): Promise<Blob> {
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, withAuth(path));
  await throwIfNotOk(res);
  return res.blob();
}

export type UploadProgressHandlers = {
  /** 已上传字节 / 总字节（仅当浏览器能计算 total 时触发） */
  onUploadProgress?: (loaded: number, total: number) => void;
  /** 浏览器已把请求体发完，等待服务端处理与响应 */
  onUploadFinished?: () => void;
};

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(
    url,
    withAuth(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
  await throwIfNotOk(res);
  return res.json() as Promise<T>;
}

/** JSON POST，带上传进度（大体积导入时请求体发送阶段可见进度） */
export async function apiPostJsonWithUploadProgress<T>(
  path: string,
  body: unknown,
  handlers?: UploadProgressHandlers,
): Promise<T> {
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const json = JSON.stringify(body);
  const { onUploadProgress, onUploadFinished } = handlers ?? {};

  const { status, statusText, responseText } = await new Promise<{
    status: number;
    statusText: string;
    responseText: string;
  }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    const token = bearerForPath(path);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Content-Type", "application/json; charset=utf-8");
    xhr.responseType = "text";
    let uploadProgressFired = false;
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && e.total > 0) {
        uploadProgressFired = true;
        onUploadProgress?.(e.loaded, e.total);
      }
    });
    xhr.upload.addEventListener("load", () => {
      if (!uploadProgressFired) onUploadProgress?.(1, 1);
      onUploadFinished?.();
    });
    xhr.addEventListener("load", () =>
      resolve({
        status: xhr.status,
        statusText: xhr.statusText,
        responseText: xhr.responseText ?? "",
      }),
    );
    xhr.addEventListener("error", () => reject(new Error("网络错误")));
    xhr.addEventListener("abort", () => reject(new Error("请求已取消")));
    xhr.send(json);
  });

  const res = new Response(responseText, { status, statusText });
  await throwIfNotOk(res);
  return JSON.parse(responseText || "null") as T;
}

/** multipart/form-data（不要手动设置 Content-Type，以便带上 boundary） */
export async function apiPostForm<T>(path: string, formData: FormData): Promise<T> {
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, withAuth(path, { method: "POST", body: formData }));
  await throwIfNotOk(res);
  return res.json() as Promise<T>;
}

/**
 * multipart POST，带 XMLHttpRequest 上传进度（fetch 无法监听 upload progress）。
 * 不要手动设置 Content-Type。
 */
export async function apiPostFormWithUploadProgress<T>(
  path: string,
  formData: FormData,
  handlers?: UploadProgressHandlers,
): Promise<T> {
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const { onUploadProgress, onUploadFinished } = handlers ?? {};

  const { status, statusText, responseText } = await new Promise<{
    status: number;
    statusText: string;
    responseText: string;
  }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    const token = bearerForPath(path);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.responseType = "text";
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && e.total > 0) onUploadProgress?.(e.loaded, e.total);
    });
    xhr.upload.addEventListener("load", () => onUploadFinished?.());
    xhr.addEventListener("load", () =>
      resolve({
        status: xhr.status,
        statusText: xhr.statusText,
        responseText: xhr.responseText ?? "",
      }),
    );
    xhr.addEventListener("error", () => reject(new Error("网络错误")));
    xhr.addEventListener("abort", () => reject(new Error("请求已取消")));
    xhr.send(formData);
  });

  const res = new Response(responseText, { status, statusText });
  await throwIfNotOk(res);
  return JSON.parse(responseText || "null") as T;
}

/**
 * 更新类请求。生产 API 经 IIS/反向代理时，HTTP PUT 常在到达 ASP.NET 前被 WebDAV 等模块拦截并返回 405；
 * 协会后台统一用 POST，并在 405/404 时回退 POST …/update 或 PUT 原路径。
 */
export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchUpdate(path, body);
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** 同上；成功且无响应体（如 200 OK 空 body） */
export async function apiPutNoContent(path: string, body: unknown): Promise<void> {
  await fetchUpdate(path, body);
}

/** 同上；响应为原始文本（如 org-structure JSON） */
export async function apiPutText(path: string, body: unknown): Promise<string> {
  const res = await fetchUpdate(path, body);
  return res.text();
}

export async function apiDelete(path: string): Promise<void> {
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, withAuth(path, { method: "DELETE" }));
  await throwIfNotOk(res);
}
